import { StatusChip, type ChipTone } from '@/components/common/StatusChip';
import type { Delegation, DnsZone, NameserverReadiness, NameserverState } from '@/features/domains/types';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@/icons';

const NS_STATE: Record<NameserverState, { tone: ChipTone; label: string }> = {
  serving: { tone: 'success', label: 'Serving' },
  stale: { tone: 'warning', label: 'Updating' },
  'not-serving': { tone: 'danger', label: 'Not serving' },
  partial: { tone: 'danger', label: 'UDP/TCP incomplete' },
  unreachable: { tone: 'neutral', label: 'Unreachable' },
  unresolvable: { tone: 'danger', label: 'No address' },
};

type Step = { label: string; tone: ChipTone; status: string; detail?: string };

/**
 * The four separate facts behind "can I point my domain at Nifty?": the zone exists, it is
 * published, every Nifty nameserver actually serves it (checked directly, the way a registrar
 * does), and what the registrar delegates to today. Delegation readiness is never inferred
 * from the database.
 */
export function DelegationReadiness({ zone, drafts, readiness, delegation, loading }: {
  zone: DnsZone; drafts: number; readiness?: NameserverReadiness; delegation?: Delegation; loading: boolean;
}) {
  const published = zone.status === 'active' && !!zone.serial;
  const steps: Step[] = [
    { label: 'Zone created', tone: 'success', status: 'Done' },
    {
      label: 'Published', tone: !published ? 'warning' : drafts ? 'warning' : 'success',
      status: !published ? 'Not published' : drafts ? `Serial ${zone.serial} · ${drafts} draft${drafts === 1 ? '' : 's'}` : `Serial ${zone.serial}`,
    },
    {
      label: 'Nameservers ready',
      tone: loading ? 'neutral' : readiness?.state === 'ready' ? 'success' : readiness?.state === 'syncing' ? 'warning' : readiness?.state === 'not-ready' ? 'danger' : 'neutral',
      status: loading ? 'Checking' : !readiness ? 'Checked after publishing'
        : readiness.state === 'ready' ? 'Both serve the zone' : readiness.state === 'syncing' ? 'Updating' : readiness.state === 'not-ready' ? 'Not ready' : 'Could not be checked',
    },
    {
      label: 'Registrar delegation',
      tone: delegation?.state === 'nifty' ? 'success' : delegation ? 'warning' : 'neutral',
      status: delegation?.state === 'nifty' ? 'Delegated to Nifty' : delegation?.state === 'partial' ? 'Mixed nameservers'
        : delegation?.state === 'external' ? `At ${delegation.nameservers.slice(0, 2).join(', ')}${delegation.nameservers.length > 2 ? '…' : ''}` : 'Unknown',
    },
  ];
  const canDelegate = readiness?.state === 'ready';

  return (
    <div className="mt-4 rounded-lg border border-border p-3">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-fg-muted">Delegation readiness</h4>
      <ol className="mt-2 grid gap-2 sm:grid-cols-4">
        {steps.map((s, i) => (
          <li key={s.label} className="rounded-md bg-surface-raised/40 p-2">
            <p className="text-xs text-fg-muted">{i + 1}. {s.label}</p>
            <div className="mt-1"><StatusChip tone={s.tone}>{s.status}</StatusChip></div>
          </li>
        ))}
      </ol>
      {readiness && (
        <ul className="mt-3 space-y-1">
          {readiness.nameservers.map((n) => (
            <li key={n.nameserver} className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-mono text-fg">{n.nameserver}</span>
              <StatusChip tone={NS_STATE[n.state].tone}>{NS_STATE[n.state].label}</StatusChip>
              <span className="text-fg-muted">{n.detail}</span>
            </li>
          ))}
        </ul>
      )}
      {readiness && delegation?.state !== 'nifty' && (
        canDelegate ? (
          <p className="mt-3 flex items-start gap-1.5 rounded-md border border-success/25 bg-success/5 p-2 text-sm text-fg">
            <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-success" />{readiness.summary}
          </p>
        ) : (
          <p className="mt-3 flex items-start gap-1.5 rounded-md border border-danger/25 bg-danger/5 p-2 text-sm text-danger" role="alert">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />{readiness.summary}
          </p>
        )
      )}
    </div>
  );
}
